import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Users } from 'lucide-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './pages/LoginPage.js';
import Dashboard from './pages/Dashboard.js';
import UsersManagement from './pages/UsersManagement.js';
import LiveSessions from './pages/LiveSessions.js';
import GiftsManagement from './pages/GiftsManagement.js';
import WalletManagement from './pages/WalletManagement.js';
import Reports from './pages/Reports.js';
import MomentsManagement from './pages/MomentsManagement.js';
import AgenciesManagement from './pages/AgenciesManagement.js';
import AgencyDetails from './pages/AgencyDetails.js';
import BDManagement from './pages/BDManagement.js';
import AgencyLogin from './pages/AgencyLogin.js';
import AgencyDashboard from './pages/AgencyDashboard.js';
import AgencyRecharge from './pages/AgencyRecharge.js';
import AgencyWallet from './pages/AgencyWallet.js';
import Layout from './components/Layout.js';
import AgencyLayout from './components/AgencyLayout.js';
import ProtectedRoute from './components/ProtectedRoute.js';

// New Admin Panel Modules
import TargetsConfiguration from './pages/TargetsConfiguration.js';
import RewardsTracking from './pages/RewardsTracking.js';
import CommissionsTracking from './pages/CommissionsTracking.js';

import SecurityLogs from './pages/SecurityLogs.js';
import SellersManagement from './pages/SellersManagement.js';
import RechargeManagement from './pages/RechargeManagement.js';
import CoinManagement from './pages/CoinManagement.js';
import CommissionManagement from './pages/CommissionManagement.js';
import FraudControl from './pages/FraudControl.js';
import SupportCenter from './pages/SupportCenter.js';
import SettingsPanel from './pages/SettingsPanel.js';
import AdminRoles from './pages/AdminRoles.js';
import SystemMonitor from './pages/SystemMonitor.js';
import MaintenancePanel from './pages/MaintenancePanel.js';
import AgencyStructure from './pages/AgencyStructure.js';
import NotificationsManagement from './pages/NotificationsManagement.js';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#e57373',
    },
    background: {
      default: '#f5f5f5',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/security-logs" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SecurityLogs />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <UsersManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/sellers" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SellersManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/recharge" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <RechargeManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/wallet" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <WalletManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/coins" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <CoinManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/commissions" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <CommissionManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/targets-config" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <TargetsConfiguration />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/rewards-tracking" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <RewardsTracking />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/commissions-tracking" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <CommissionsTracking />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/fraud-control" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <FraudControl />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/live-sessions" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <LiveSessions />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/gifts" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <GiftsManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <Reports />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SupportCenter />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/notifications" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <NotificationsManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SettingsPanel />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/roles" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AdminRoles />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/system-monitor" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SystemMonitor />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/maintenance" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <MaintenancePanel />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/moments" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <MomentsManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agencies" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AgenciesManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agencies/:id" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AgencyDetails />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bd-management" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <BDManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agency-structure" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AgencyStructure />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Agency Panel Routes */}
          <Route path="/agency/login" element={<AgencyLogin />} />
          <Route path="/agency/dashboard" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencyDashboard />
              </AgencyLayout>
            </ProtectedRoute>
          } />
          <Route path="/agency/recharge" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencyRecharge />
              </AgencyLayout>
            </ProtectedRoute>
          } />
          <Route path="/agency/hosts" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <div className="text-white p-20 text-center border-2 border-dashed border-white/10 rounded-[40px] animate-pulse">
                  <Users size={64} className="mx-auto mb-6 text-slate-700" />
                  <h2 className="text-3xl font-black mb-4">Host Management</h2>
                  <p className="text-slate-500 max-w-md mx-auto">This section is currently being integrated with the live host database. Stay tuned for the update.</p>
                </div>
              </AgencyLayout>
            </ProtectedRoute>
          } />
          <Route path="/agency/wallet" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencyWallet />
              </AgencyLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;